import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import {
  PhoneCall,
  CalendarCheck,
  Target,
  Users,
  Repeat,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Clock,
  GraduationCap,
  DollarSign,
  Languages,
  // Phone removed from hero
  Home,
  Sun,
  Hammer,
  Wrench,
  Umbrella,
  Briefcase,
  HardHat,
  TrendingUp,
} from "lucide-react";
import PipelinePreview from "@/components/home/PipelinePreview";
import LeadForm from "@/components/home/LeadForm";
import FounderVideo from "@/components/home/FounderVideo";
import TrustBar from "@/components/home/TrustBar";
import WhyElevate from "@/components/home/WhyElevate";
import FounderAuthority from "@/components/home/FounderAuthority";
import DashboardShowcase from "@/components/home/DashboardShowcase";
import ElevateMomentSection from "@/components/home/ElevateMomentSection";
import SkylineOverlay from "@/components/brand/SkylineOverlay";
import PricingAnchor from "@/components/PricingAnchor";

const focusedServices = [
  {
    icon: PhoneCall,
    title: "Real Estate Cold Calling",
    body: "180, 250 dials per caller / day on absentee, probate, pre-foreclosure, and tax-delinquent lists.",
    to: "/services/real-estate-cold-calling",
  },
  {
    icon: CalendarCheck,
    title: "Appointment Setting",
    body: "Trained setters qualify motivated sellers and book live calls straight into your calendar.",
    to: "/services/appointment-setting",
  },
  {
    icon: Target,
    title: "Acquisition Support",
    body: "Acquisition managers comp deals, run seller calls, and lock contracts so you can stay in CEO mode.",
    to: "/services/acquisition-managers",
  },
  {
    icon: Users,
    title: "Virtual Assistants",
    body: "REI-trained VAs for list pulling, skip tracing, dispositions, and transaction coordination.",
    to: "/services/real-estate-virtual-assistants",
  },
  {
    icon: Repeat,
    title: "CRM Follow-Up",
    body: "Long-term nurture in GoHighLevel, Podio, or FUB. We turn dead leads into closed deals.",
    to: "/services/investor-follow-up",
  },
];

const steps = [
  { n: "01", t: "Discovery Call", b: "30 min. We map your buy box, market, lead source, and pipeline gaps." },
  { n: "02", t: "Team Setup", b: "We hand-pick US-fluent talent and assign a dedicated team lead to your account." },
  { n: "03", t: "Script & CRM Integration", b: "Your scripts, your CRM, your dialer. SOPs documented and tested before launch." },
  { n: "04", t: "Campaign Launch", b: "Live dials within 10, 14 days. Daily activity logs from day one." },
  { n: "05", t: "KPI Reporting & Optimization", b: "Weekly scorecard, call QA, and coaching tied to your conversion numbers." },
];

const industries = [
  { icon: Home, name: "Real Estate", flagship: true },
  { icon: HardHat, name: "Roofing" },
  { icon: Sun, name: "Solar" },
  { icon: Wrench, name: "Home Services" },
  { icon: Umbrella, name: "Insurance" },
  { icon: Briefcase, name: "Professional Services" },
  { icon: Hammer, name: "Construction" },
  { icon: TrendingUp, name: "Other Sales-Driven Businesses" },
];

const whyEgypt = [
  { icon: GraduationCap, t: "Educated workforce", b: "University-trained talent with sales, support, and operations backgrounds." },
  { icon: Languages, t: "Neutral American English", b: "Tone-tested for US homeowner calls. No script-reading robot energy." },
  { icon: Target, t: "REI-trained from day one", b: "Comps, motivation, buy boxes, dispositions, and the major REI CRMs." },
  { icon: Clock, t: "Full US-hours coverage", b: "EST / CST / MST / PST shifts. Your team works when your sellers answer." },
  { icon: DollarSign, t: "~70% lower fully-loaded cost", b: "Compared to a domestic hire with the same training and KPIs." },
  { icon: ShieldCheck, t: "Strong work ethic", b: "Low turnover, dedicated full-time seats, replacement guarantee on every role." },
];


const homepageFaqs = [
  {
    q: "Who is Elevate Global Solutions for?",
    a: "US-based real estate investors, wholesalers, cash buyers, acquisition companies, and brokerages who want to scale acquisition without paying full US payroll.",
  },
  {
    q: "How fast can a team launch?",
    a: "10, 14 days from kickoff call to first dials. Pipeline managers and TCs add 1 extra week for SOP build.",
  },
  {
    q: "Do your team members work US business hours?",
    a: "Yes. EST, CST, MST, or PST. Standard shift is 9am, 6pm in your local time zone.",
  },
  {
    q: "What does it cost?",
    a: "Starting at ~$1,495/seat/month for cold callers, ~$1,795 for setters, and ~$2,495 for acquisition managers. Final pricing depends on hours, volume, and stack, typically 60, 70% less than a comparable US hire fully loaded. You'll get an exact quote on the free strategy call.",
  },
  {
    q: "What CRMs and dialers do you support?",
    a: "GoHighLevel, Podio, FollowUpBoss, REI Reply, HubSpot, Salesforce, plus Mojo, Smarter Contact, Batch Dialer, PhoneBurner, ReadyMode, and CallTools.",
  },
  {
    q: "Do you guarantee deals?",
    a: "No ethical operator guarantees deals. We guarantee dial volume, contact rates, lead quality standards, and replace any underperforming team member at no cost.",
  },
];

const Index = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: homepageFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Lead Gen & Appointment Setting Teams | Elevate Global</title>
        <meta
          name="description"
          content="Lead generation, cold calling, appointment setting, and CRM follow-up teams that help businesses grow. Real estate flagship expertise, multi-industry execution."
        />
        <meta property="og:title" content="Lead Generation & Appointment Setting Teams That Help Businesses Grow" />
        <meta property="og:description" content="Cold calling, follow-up, and appointment setting teams built for growth-focused companies. Real estate expertise, multi-industry execution." />
        <meta property="og:url" content="https://www.elevateglobalsolutions.net/" />
        <meta property="og:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:title" content="Lead Generation & Appointment Setting Teams That Help Businesses Grow" />
        <meta name="twitter:description" content="Cold calling, follow-up, and appointment setting teams built for growth-focused companies. Real estate expertise, multi-industry execution." />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden bg-primary">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(var(--secondary)) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <SkylineOverlay opacityClass="opacity-[0.07]" />
        <div className="container-narrow relative section-padding py-20 md:py-28 lg:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr]">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-foreground/95">
                <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
                Managed Acquisitions Infrastructure for US Real Estate
              </span>
              <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] text-primary-foreground md:text-5xl lg:text-[3.5rem]">
                Managed Real Estate{" "}
                <span className="text-secondary">Cold Calling Teams</span>
              </h1>
              <p className="mt-5 font-display text-xl font-semibold text-primary-foreground/95 md:text-2xl">
                Stop managing VAs. Start closing deals.
              </p>
              <p className="mt-6 max-w-xl text-base text-primary-foreground/90 md:text-lg">
                Deploy a fully managed, US-trained outbound sales infrastructure built specifically for real estate acquisitions. We handle the data, the dialers, and the daily management tax so your local agents can focus exclusively on closing motivated sellers. 100% CRM integration included.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/book">
                  <Button
                    size="lg"
                    className="bg-secondary text-secondary-foreground hover:bg-secondary/90 cta-glow font-bold text-base px-7 h-12 shadow-lg shadow-secondary/30 ring-2 ring-secondary/40"
                  >
                    Book a Strategy Call
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/25 bg-transparent text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
                  >
                    See Pricing
                  </Button>
                </a>
              </div>
              <p className="mt-3 text-xs font-medium text-primary-foreground/70">
                Your call is framed as a <span className="font-semibold text-secondary">Free 30-Minute Lead Flow Audit</span>, not a sales pitch.
              </p>

              {/* Trust & Integration Bar */}
              <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-primary-foreground/70">
                    Seamlessly integrates with your existing tech stack
                  </span>
                  <span className="inline-flex items-center gap-1.5 self-start rounded-full border border-secondary/30 bg-secondary/10 px-2.5 py-1 text-[10px] font-semibold text-secondary sm:self-auto">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-secondary" />
                    Active in 32 US markets
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    "Follow Up Boss",
                    "GoHighLevel",
                    "Podio",
                    "Mojo Dialer",
                    "BatchDialer",
                    "Smarter Contact",
                  ].map((logo) => (
                    <span
                      key={logo}
                      className="inline-flex items-center rounded-md border border-white/15 bg-white/[0.06] px-2.5 py-1.5 text-xs font-semibold text-primary-foreground/90"
                    >
                      {logo}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:pl-6">
              <PipelinePreview />
            </div>
          </div>
        </div>
      </section>


      {/* TRUST BAR */}
      <TrustBar />

      {/* KPI strip */}
      <section className="border-b border-border bg-card">
        <div className="container-narrow grid grid-cols-2 gap-6 px-4 py-10 md:grid-cols-4 md:px-8 lg:px-16">
          {[
            { v: "180, 250", l: "Dials per caller / day" },
            { v: "10, 14 days", l: "Team launch time" },
            { v: "~70%", l: "Lower cost vs US hire" },
            { v: "50 states", l: "Markets covered" },
          ].map((k) => (
            <div key={k.l}>
              <div className="font-display text-2xl font-extrabold text-primary md:text-3xl">{k.v}</div>
              <div className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">{k.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES (5 focused) */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="max-w-2xl">
            <div className="gold-divider" />
            <h2 className="mt-4 font-display text-3xl font-bold text-foreground md:text-4xl">
              Trained teams that fill your pipeline.
            </h2>
            <p className="mt-3 text-muted-foreground">
              Cold callers, setters, acquisition managers, VAs, and CRM follow-up specialists &mdash;
              built on years of real estate execution and applied across multiple industries.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {focusedServices.map((s) => (
              <Link
                key={s.title}
                to={s.to}
                className="group rounded-xl border border-border bg-card p-6 transition-all hover:border-secondary hover:shadow-md"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-accent text-primary">
                  <s.icon size={20} />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                <div className="mt-5 inline-flex items-center text-sm font-semibold text-primary">
                  <span>Explore {s.title.toLowerCase()}</span>
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES WE SERVE */}
      <section className="section-padding bg-accent/40">
        <div className="container-narrow">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <div className="gold-divider" />
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-secondary">
                Real Estate Expertise. Multi-Industry Execution.
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold text-foreground md:text-4xl">
                Industries we serve
              </h2>
              <p className="mt-3 text-muted-foreground">
                Real estate is our flagship market &mdash; the playbook we&rsquo;ve refined over
                thousands of dials now powers growth-focused teams across home services, solar,
                roofing, insurance, and professional services.
              </p>
            </div>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((ind) => (
              <div
                key={ind.name}
                className={`relative rounded-xl border bg-card p-5 transition-all hover:shadow-md ${
                  ind.flagship
                    ? "border-secondary shadow-sm ring-1 ring-secondary/30"
                    : "border-border hover:border-secondary/60"
                }`}
              >
                {ind.flagship && (
                  <span className="absolute right-3 top-3 inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-secondary-foreground">
                    Flagship
                  </span>
                )}
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-primary">
                  <ind.icon size={18} />
                </div>
                <h3 className="mt-4 font-display text-base font-bold text-foreground">
                  {ind.name}
                </h3>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm text-muted-foreground">
            Don&rsquo;t see your industry? If your business runs on outbound conversations and
            booked appointments, our teams can execute.{" "}
            <Link to="/contact" className="font-semibold text-primary hover:text-secondary">
              Talk to us &rarr;
            </Link>
          </p>
        </div>
      </section>

      {/* WHY ELEVATE vs GENERIC OUTSOURCING */}
      <WhyElevate />

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="section-padding bg-primary text-primary-foreground">
        <div className="container-narrow">
          <div className="max-w-2xl">
            <div className="h-px w-12 bg-secondary" />
            <h2 className="mt-4 font-display text-3xl font-bold text-primary-foreground md:text-4xl">
              How we build your acquisition team
            </h2>
            <p className="mt-3 text-primary-foreground/90">
              A repeatable operational rollout, from kickoff to live dials in two weeks.
            </p>
          </div>

          <ol className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
            {steps.map((s, i) => (
              <li
                key={s.n}
                className="relative rounded-xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur"
              >
                <span className="font-display text-3xl font-extrabold text-secondary">{s.n}</span>
                <h3 className="mt-3 font-display text-base font-bold text-primary-foreground">{s.t}</h3>
                <p className="mt-2 text-sm text-primary-foreground/90">{s.b}</p>
                {i < steps.length - 1 && (
                  <span className="absolute right-2 top-1/2 hidden h-px w-4 -translate-y-1/2 bg-secondary/40 lg:block" />
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHY EGYPTIAN TALENT */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <div className="gold-divider" />
              <h2 className="mt-4 font-display text-3xl font-bold text-foreground md:text-4xl">
                Why we build teams in Egypt
              </h2>
              <p className="mt-4 text-muted-foreground">
                Egypt is one of the most underrated talent markets in the world for US-facing sales
                operations, a large, educated, English-fluent workforce with a strong work ethic
                and full US-hours availability.
              </p>
              <p className="mt-3 text-muted-foreground">
                We hand-pick the top 3% and train them specifically on US real estate acquisition
                before they ever pick up the phone for your account.
              </p>
              <Link to="/about" className="mt-6 inline-flex items-center text-sm font-semibold text-primary hover:text-secondary">
                Read the founder story
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {whyEgypt.map((w) => (
                <div key={w.t} className="rounded-xl border border-border bg-card p-5">
                  <w.icon className="h-5 w-5 text-secondary" />
                  <h3 className="mt-3 font-display text-base font-bold text-foreground">{w.t}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{w.b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING ANCHOR, transparent starting prices */}
      <PricingAnchor />



      {/* FOUNDER AUTHORITY */}
      <FounderAuthority />

      {/* FOUNDER VIDEO (hidden until a real video URL is set in src/config/media.ts) */}
      <FounderVideo />

      {/* DASHBOARD SHOWCASE, Visibility Into Every Lead (hidden until real screenshots are added) */}
      <DashboardShowcase />

      {/* FAQ */}
      <section className="section-padding">
        <div className="container-narrow max-w-3xl">
          <div className="gold-divider" />
          <h2 className="mt-4 font-display text-3xl font-bold text-foreground md:text-4xl">
            Frequently asked
          </h2>
          <div className="mt-8 space-y-3">
            {homepageFaqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-5 transition-colors hover:border-secondary/50">
                <summary className="cursor-pointer list-none text-base font-semibold text-foreground">
                  <span className="flex items-center justify-between gap-3">
                    {f.q}
                    <span className="text-secondary transition-transform group-open:rotate-45">+</span>
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA + LEAD FORM */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="relative overflow-hidden rounded-3xl bg-primary p-8 md:p-14">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, hsl(var(--secondary)) 1px, transparent 0)",
                backgroundSize: "32px 32px",
              }}
            />
            <div className="relative grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
              <div>
                <div className="h-px w-12 bg-secondary" />
                <h2 className="mt-4 font-display text-3xl font-bold text-primary-foreground md:text-4xl">
                  Book your free strategy call
                </h2>
                <p className="mt-4 text-primary-foreground/75">
                  30 minutes. We'll review your buy box, your market, and what an acquisition team
                  would look like for your operation, whether you hire us or not.
                </p>
                <ul className="mt-6 space-y-2 text-sm text-primary-foreground/85">
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" /> No contracts. Month-to-month engagements.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" /> Walk away with a pipeline plan you can use either way.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" /> US-hours coverage, full-time dedicated teams.</li>
                </ul>
              </div>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      <ElevateMomentSection />
    </>

  );
};

export default Index;
